/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import first from 'lodash/first';
import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
import set from 'lodash/set';
import size from 'lodash/size';
import { gnoment } from 'shared-ui/gnoment';
import {
    Button,
    ProgressIndicator,
    SecurityHelper,
    NeedToKnow,
    Heading,
    ContentBlock,
    Box,
    Spacer,
} from 'sarsaparilla';
import { withCompatRouter, COMPAT_ROUTER_PROP_TYPES } from 'sarsaparilla/legacy';
import {
    OrderDetailsLayout,
    OrderDetailsTimerNotice,
    OrderDetailsSection,
    OrderDetailsSummary,
} from 'shared-ui/components/OrderDetailsLayout';
import { gaReportingCartReservationsForStepper } from 'shared-ui/utils/gaCommon';
import { updateAccount } from 'ui-account/src/actions/profileActions';
import * as cartActions from 'ui-cart/src/actions/cartActions';
import FlexFieldHOC from 'ui-permit/shared-components/registration/FlexField';
import VehicleListWrapper from 'ui-hunting-permit/src/components/registration/VehicleList';
import { isIssuanceFromAwardedLottery } from 'ui-permit/shared-components/utils/issuance-utils';
import ErrorNotification from 'ui-permit/shared-components/common/ErrorNotification';
import { scrollUp } from 'ui-permit/src/utils/utilsNew';
import { fetchReservationSummaryImageUrl } from 'ui-permit/shared-components/actions/actions';
import {
    GROUP_DETAIL_PROP_PATH,
    GROUP_DETAIL_FIRST_NAME_PROP_PATH,
    GROUP_DETAIL_LAST_NAME_PROP_PATH,
    GROUP_DETAIL_EMAIL_PROP_PATH,
    GROUP_DETAIL_PHONE_PROP_PATH,
    GROUP_DETAIL_ADDRESS1_PROP_PATH,
    GROUP_DETAIL_ADDRESS2_PROP_PATH,
    GROUP_DETAIL_CITY_PROP_PATH,
    GROUP_DETAIL_COUNTRY_PROP_PATH,
    GROUP_DETAIL_STATE_PROP_PATH,
    GROUP_DETAIL_ZIPCODE_PROP_PATH,
    NEED_TO_KNOW_CHECKED_PROP_PATH,
} from 'ui-permit/shared-components/constants/propPaths';

import {
    MATTAMUSKEET_NWR_WATERFOWL,
    MATTAMUSKEET_DISABLED_WATERFOWL,
    MATTAMUSKEET_YOUTH_WATERFOWL,
} from 'ui-permit/src/constants';
import {
    availabilitySelectors,
    permitSelectors,
    reservationSelectors,
    getErrors,
} from '../reducers';
import { selectors as sharedSelectors } from '../reducers/shared';
import { availabilityPageRoute } from '../../dev/routesArray';
import { getPermitTypeName, isIssuedPermit } from '../utils';
import issuanceShape from '../utils/issuanceShape';
import GroupDetail from '../components/registration/GroupDetail';
import ReservationSummary from '../components/registration/ReservationSummary';
import * as availabilityActions from '../actions/availability';
import * as permitActions from '../actions/permit';
import * as reservationActions from '../actions/reservation';
import * as seasonActions from '../actions/season';
import {
    GROUP_DETAIL_PASS_TYPE_PROP_PATH,
    GROUP_DETAIL_PASS_ID_PROP_PATH,
    DNR_NUMBER_PROP_PATH,
    RES_ID_DELIMITER,
    progressSteps,
} from '../constants';
import { LOCATION_TZ } from '../constants/hunting-permit-constants';

import GroupSizeHOC from '../components/registration/GroupSize';
import { readMaxGroupSizeRule } from '../constants/registration';

const databindings = {
    [GROUP_DETAIL_PROP_PATH]: 'group_members',
    [GROUP_DETAIL_FIRST_NAME_PROP_PATH]: 'first_name',
    [GROUP_DETAIL_LAST_NAME_PROP_PATH]: 'last_name',
    [GROUP_DETAIL_EMAIL_PROP_PATH]: 'email',
    [GROUP_DETAIL_PHONE_PROP_PATH]: 'cell_phone',

    [GROUP_DETAIL_ADDRESS1_PROP_PATH]: 'home_address.address1',
    [GROUP_DETAIL_ADDRESS2_PROP_PATH]: 'home_address.address2',
    [GROUP_DETAIL_CITY_PROP_PATH]: 'home_address.city',
    [GROUP_DETAIL_COUNTRY_PROP_PATH]: 'home_address.country',
    [GROUP_DETAIL_STATE_PROP_PATH]: 'home_address.state',
    [GROUP_DETAIL_ZIPCODE_PROP_PATH]: 'home_address.zip_code',

    [GROUP_DETAIL_PASS_ID_PROP_PATH]: 'pass_id',
    [GROUP_DETAIL_PASS_TYPE_PROP_PATH]: 'pass_type',

    [DNR_NUMBER_PROP_PATH]: 'extra_fields.dnr_number',

    [NEED_TO_KNOW_CHECKED_PROP_PATH]: 'need_to_know_checked',
};

const groupDetail = GroupDetail(issuanceShape, databindings);

const groupSizeOnly = GroupSizeHOC(null, databindings);

const needToKnowValidationError = {
    props: { controlName: 'Need To Know', message: ' is required' },
};

const needToKnow = ({ permit, reservation, pActions, needToKnowRef }) => {
    const checkedPath = databindings[NEED_TO_KNOW_CHECKED_PROP_PATH];
    const isChecked = get(reservation, checkedPath, false);
    const handleChange = () => {
        const newChecked = !isChecked;
        pActions.updatePendingReservation(undefined, checkedPath, newChecked);
        pActions.formValidationChange(
            `${reservation.id}_${checkedPath}`,
            newChecked ? null : needToKnowValidationError
        );
    };
    return (
        <NeedToKnow
            ref={needToKnowRef}
            isRequired
            siteName={permit.name}
            siteInfo={get(permit, 'description.need_to_know_order')}
            isChecked={isChecked}
            onChange={handleChange}
        />
    );
};

const permitComponentMapping = {
    flex_fields: { component: FlexFieldHOC(), precedence: 1 },
    vehicle_list: { component: VehicleListWrapper, precedence: 2 },
    total_group_size: { component: groupSizeOnly, precedence: 3 },
};
const groupDetailListComponent = { component: groupDetail, precedence: 0 };
const needToKnowComponent = { component: needToKnow, precedence: 9999 };

let componentMapping = [groupDetail, needToKnow];

export class OrderDetailsPage extends React.Component {
    static propTypes = {
        dispatch: PropTypes.func,
        cart: PropTypes.any,
        permit: PropTypes.object /*PermitType*/,
        pActions: PropTypes.object,
        rActions: PropTypes.object,
        sActions: PropTypes.object,
        cActions: PropTypes.object,
        aActions: PropTypes.object,
        fetchImageUrl: PropTypes.func,
        router: COMPAT_ROUTER_PROP_TYPES,
        validation: PropTypes.object /*ValidationType*/,
        imageUrl: PropTypes.string,
        reservation: PropTypes.object /*ReservationType*/,
    };

    state = {
        totalGroupSize: PropTypes.number,
    };

    constructor(props) {
        super(props);
        this.state = {
            reservation: this.props.reservation,
            isLoadingReservation: true,
            totalGroupSize: 1,
        };
        this.needToKnowRef = React.createRef();
    }

    async componentDidMount() {
        // fetch first issuance from the list of reservationids and initialize
        // this page allows bulk editing of "initial" issuances (if there are multiple)
        const { permitId, reservationId } = this.props.router.params;
        const issuanceId = reservationId.replace(`${RES_ID_DELIMITER}${permitId}`, '');

        const [seasonResults, reservationResults] = await Promise.all([
            this.props.sActions.fetchSeasons({
                permitId,
                currentDate: gnoment.tz(LOCATION_TZ[permitId]).format('YYYY-MM-DD'),
            }),
            this.props.rActions.fetchUnpaidReservation(permitId, issuanceId),
            this.props.pActions.fetchPermit(permitId),
            this.props.cActions.fetchCart(),
            this.props.fetchImageUrl(permitId),
        ]);

        await this.props.aActions.fetchAvailability({
            permitId,
            type: reservationResults?.permit_type,
            year: first(seasonResults)?.season_year,
        });

        this.setState({ isLoadingReservation: false, isLoadingCart: false });

        this.setState({
            totalGroupSize: reservationResults?.extra_fields?.group_size || 1,
        });
    }

    componentDidUpdate() {
        if (this.props.reservation.id && !this.state.reservation.id) {
            // setState is OK if wrapped in non-conflicting conditional
            // eslint-disable-next-line react/no-did-update-set-state
            this.setState({ reservation: cloneDeep(this.props.reservation) });
        }
    }

    isThisFormValid = () => {
        const needToKnowValidation = this.needToKnowRef.current?.validate?.() ?? {};
        const { summary } = this.props.validation;
        const needToKnowCheckedPath = databindings[NEED_TO_KNOW_CHECKED_PROP_PATH];
        this.props.pActions.formValidationChange(
            `${this.props.reservation.id}_${needToKnowCheckedPath}`,
            needToKnowValidation.isInvalid ? needToKnowValidationError : null
        );
        return !size(summary) && !needToKnowValidation.isInvalid;
    };

    addToCart = async ({ navigate = true } = {}) => {
        const { pActions, rActions } = this.props;
        const { reservation } = this.state;

        if (!this.isThisFormValid()) {
            pActions.setDisplayValidationErrors();
            scrollUp();
            return;
        }
        this.setState({ isLoadingCart: true });

        set(reservation, 'extra_fields.quota_type', 'advanced');
        set(reservation, 'extra_fields.category', 'non-commercial');
        set(reservation, 'permit_holder', reservation.group_members[0]);

        try {
            await rActions.updateReservation(reservation);
            this.setState({ isLoadingCart: false });
            if (navigate) {
                this.goToCart();
            }
            return true;
        } catch {
            this.setState({ isLoadingCart: false });
            scrollUp();
        }
    };

    continueShopping = () =>
        this.props.router.navigate(`/permits/${this.props.router.params.permitId}`);

    goToCart = () => window.location.assign('/cart');

    goToMyReservations = () => window.location.assign('/account/reservations');

    removeFromCart = () => {
        const { cActions } = this.props;
        cActions
            .removeReservation(this.props.router.params.reservationId)
            .then(this.goToCart)
            .catch(scrollUp);
    };

    updatePendingReservation = (id, path, value) =>
        this.setState(({ reservation }) => ({
            reservation: set(cloneDeep(reservation), path, value),
        }));

    render() {
        const { reservation } = this.state;
        const { dates, cart, errors, pActions, validation, permit, imageUrl } =
            this.props;

        const account = SecurityHelper.getAccount();

        const displayValidationError = validation?.displayValidationError;
        pActions.updatePendingReservation = this.updatePendingReservation;

        const cartReservation = cart.reservations.find(
            ({ reservation_id }) =>
                reservation.id === reservation_id.replace(`__${permit.id}`, '')
        );

        // TODO: need to update this in the future if the awarded reservation allows you to go to more than 1 date
        const filteredDates = isIssuanceFromAwardedLottery(reservation)
            ? dates.filter((date) => date === reservation.start_date)
            : dates;

        // use this for both hiding group members AND calculating the fee per member. May need to separate in the future.
        const showGroupMembersSection = ![
            // not adding group member name section for these permits
            MATTAMUSKEET_NWR_WATERFOWL,
            MATTAMUSKEET_DISABLED_WATERFOWL,
            MATTAMUSKEET_YOUTH_WATERFOWL,
        ].includes(permit.id);

        const maxGroupSize = readMaxGroupSizeRule(
            permit.id,
            reservation.permit_type,
            permit.rules
        );

        const updateGroupSize = (newSize) => {
            pActions.updatePendingReservation(
                reservation.id,
                'extra_fields.group_size',
                newSize
            );
            this.setState({ totalGroupSize: newSize });
        };

        const props = {
            dates: filteredDates,
            cartReservation,
            permit,
            pActions,
            reservation,
            validation,
            displayValidationError,
            hasPermission: true,
            account,
            permitTypeName: getPermitTypeName(permit, reservation.permit_type),
            modifyLink: availabilityPageRoute(
                reservation.permit_id,
                reservation.permit_type,
                reservation.start_date,
                get(reservation, 'group_members', []).length,
                reservation.id
            ),
            needToKnowRef: this.needToKnowRef,

            // group size component props
            totalGroupSize: this.state.totalGroupSize,
            maxSize: maxGroupSize,
            showGroupMembersList: showGroupMembersSection,
            onGroupSizeChange: updateGroupSize,
        };

        if (!reservation.id && errors.reservation) {
            return (
                <div className="permit-content-outer-wrap mt-8 mb-8">
                    <ErrorNotification
                        className="permit-error-wrap mt-8 mb-8"
                        errors={[
                            `Sorry, reservation "${this.props.router.params.reservationId}" could not be found.`,
                        ]}
                    />
                    <Button
                        shouldFitContainer
                        appearance="link"
                        className="mt-1"
                        size="lg"
                        onClick={this.goToMyReservations}
                    >
                        View My Reservations
                    </Button>
                </div>
            );
        }

        let formComponents = null;
        if (!this.state.isLoadingReservation) {
            const permitComponentsList = permit?.components
                ? Object.keys(permit.components).reduce((result, componentName) => {
                      if (permit.components[componentName]) {
                          const componentFound = permitComponentMapping[componentName];
                          if (componentFound) {
                              result.push(componentFound);
                          }
                      }
                      return result;
                  }, [])
                : null;

            if (permitComponentsList && permitComponentsList.length > 0) {
                permitComponentsList.push(
                    groupDetailListComponent, // 'group detail' is always present and the first component
                    needToKnowComponent // 'need to know' is always present and the last component
                );

                componentMapping = permitComponentsList
                    .sort((a, b) => a.precedence - b.precedence)
                    .map((item) => item.component);
            }

            formComponents = componentMapping.map((Component, sindx) => {
                return <Component {...props} key={`order_detail_component_${sindx}`} />;
            });
        }

        const validationErrors =
            validation && validation?.summary && displayValidationError
                ? validation?.summary?.map((v) => {
                      return `${v?.error?.props?.controlName}${v?.error?.props?.message}`;
                  })
                : [];

        const summary = (
            <OrderDetailsSummary
                isLoading={this.state.isLoadingReservation}
                heading={permit.name}
                imageUrl={imageUrl}
                isWaitingForServerResponse={
                    this.state.isLoadingReservation || this.state.isLoadingCart
                }
                parentName={permit.name}
                isProceedToCartDisabled={false}
                onProceedToCartClick={this.addToCart}
                proceedToCartButtonText="Proceed to Cart"
                secondaryButtonText="Continue shopping"
                onSecondaryButtonClick={this.continueShopping}
                showBuyNow
                reservationId={this.props.router.params.reservationId}
            >
                <ReservationSummary
                    cart={cart}
                    cartReservation={cartReservation}
                    permit={permit}
                    reservations={[this.state.reservation]}
                    totalGroupSize={this.state.totalGroupSize}
                    calcFeePerMember={!showGroupMembersSection}
                />
            </OrderDetailsSummary>
        );

        return (
            <div className="hunting-permit-order-details-page">
                <Box background="gray-2" paddingY="sm">
                    <ProgressIndicator
                        eventToReportToGA={gaReportingCartReservationsForStepper(
                            cart?.reservations,
                            progressSteps,
                            0
                        )}
                        shouldReportToGA
                        steps={progressSteps}
                        activeStep={1}
                    />
                </Box>
                <Spacer size="xl" />
                <ContentBlock>
                    <OrderDetailsLayout summaryElement={summary}>
                        <ErrorNotification
                            className="permit-error-wrap"
                            errors={[errors.reservation, ...validationErrors]}
                        />
                        <Heading headingLevel={1} appearance="h4" className="mb-2">
                            Order Details
                        </Heading>
                        <OrderDetailsTimerNotice />

                        <OrderDetailsSection
                            heading={permit.name}
                            onRemoveClick={
                                !isIssuedPermit(reservation) ? this.removeFromCart : null
                            }
                            isLoading={this.state.isLoadingReservation}
                        >
                            {formComponents}
                        </OrderDetailsSection>
                    </OrderDetailsLayout>
                </ContentBlock>
            </div>
        );
    }
}

OrderDetailsPage.displayName = 'OrderDetailsPage';

function select(state) {
    return {
        permit: permitSelectors.getPermit(state),
        dates: availabilitySelectors.getDates(state),
        reservation: reservationSelectors.getReservation(state),
        errors: getErrors(state, 'reservation'),
        cart: state.cart,
        validation: state.huntingpermit.validations,
        imageUrl: sharedSelectors.getImageUrlSelector(state),
    };
}

function mapDispatchToProps(dispatch) {
    return {
        aActions: bindActionCreators(availabilityActions, dispatch),
        cActions: bindActionCreators(cartActions, dispatch),
        pActions: bindActionCreators(permitActions, dispatch),
        rActions: bindActionCreators(reservationActions, dispatch),
        sActions: bindActionCreators(seasonActions, dispatch),
        updateAccount: bindActionCreators(updateAccount, dispatch),
        fetchImageUrl: bindActionCreators(fetchReservationSummaryImageUrl, dispatch),
    };
}

export default withCompatRouter(connect(select, mapDispatchToProps)(OrderDetailsPage));

// cSpell:ignore ZIPCODE, databindings, reservationids, sindx, huntingpermit
